import { useCallback, useEffect, useMemo, useState } from "react";
import type { CuadraChatClient } from "../lib/cuadraChatClient";
import type { ModelPage } from "../types/cuadra";
import type { ModelOption } from "../components/thread/types";

export interface UseModelsResult {
  modelsData: ModelPage | null;
  modelsLoading: boolean;
  modelsError: Error | null;
  models: ModelOption[];
  fetchModels: () => Promise<void>;
}

/**
 * Fetches and manages the list of available models.
 * Only active when `modelMode === 'selector'`.
 *
 * @param modelTierAccess - Comma-separated tiers the plan can use (e.g. 'standard,premium').
 *   Models whose `modelTier` is not in the list are marked `locked: true`.
 */
export function useModels(
  client: CuadraChatClient,
  modelMode: "selector" | "fixed",
  modelTierAccess?: string,
): UseModelsResult {
  const [modelsData, setModelsData] = useState<ModelPage | null>(null);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelsError, setModelsError] = useState<Error | null>(null);

  const fetchModels = useCallback(async () => {
    if (modelMode !== "selector") return;

    setModelsLoading(true);
    setModelsError(null);
    try {
      const data = await client.listModels();
      setModelsData(data);
    } catch (error) {
      setModelsError(
        error instanceof Error ? error : new Error("Failed to load models"),
      );
    } finally {
      setModelsLoading(false);
    }
  }, [client, modelMode]);

  useEffect(() => {
    if (modelMode === "selector") {
      fetchModels();
    }
  }, [modelMode, fetchModels]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      (
        window as Window & { __cuadraUIKitRefetchModels?: typeof fetchModels }
      ).__cuadraUIKitRefetchModels = fetchModels;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete (
          window as Window & { __cuadraUIKitRefetchModels?: typeof fetchModels }
        ).__cuadraUIKitRefetchModels;
      }
    };
  }, [fetchModels]);

  const allowedTiers = useMemo(() => {
    if (!modelTierAccess) return null;
    return new Set(modelTierAccess.split(",").map((t) => t.trim()));
  }, [modelTierAccess]);

  const models: ModelOption[] = useMemo(
    () =>
      (modelsData?.items || [])
        .filter((m) => m?.id && m?.displayName)
        .map((m) => {
          const tier = (m.modelTier as "standard" | "premium") ?? "standard";
          return {
            id: m.id as string,
            name: m.displayName as string,
            supportsVision: m.supportsVision ?? false,
            supportsReasoning: m.supportsReasoning ?? false,
            modelTier: tier,
            locked: allowedTiers ? !allowedTiers.has(tier) : false,
          };
        }),
    [modelsData, allowedTiers],
  );

  return { modelsData, modelsLoading, modelsError, models, fetchModels };
}
